import React, { useMemo, useState } from "react";
import { ChevronRight, GitBranch, Link2, Plus, Shield, UserCog } from "lucide-react";
import type {
  Permission,
  Resource,
  RoleResource,
  RoleResourcePermission,
  VmsRole,
} from "../../features/rbac/types";
import { ConfirmDialog } from "./shared/ConfirmDialog";
import { EmptyState } from "./shared/EmptyState";
import { FormModal, LabeledField } from "./shared/FormModal";
import { SectionCard } from "./shared/SectionCard";
import { StatusPill } from "./shared/StatusPill";
import { useRbacAction } from "./hooks/useRbacAction";

interface RbacHierarchyFlowProps {
  vmsRoles: VmsRole[];
  roleResources: RoleResource[];
  roleResourcePermissions: RoleResourcePermission[];
  resources: Resource[];
  permissions: Permission[];
  onCreateMapping: (payload: {
    roleResourceId: number;
    resourceIds: number[];
    permissionIds: number[];
  }) => void;
  onDeleteMapping: (mappingId: number) => void;
  onOpenAdvancedMappings: () => void;
}

type Step = 1 | 2 | 3;

export const RbacHierarchyFlow: React.FC<RbacHierarchyFlowProps> = ({
  vmsRoles,
  roleResources,
  roleResourcePermissions,
  resources,
  permissions,
  onCreateMapping,
  onDeleteMapping,
  onOpenAdvancedMappings,
}) => {
  const { matches, isBusy } = useRbacAction();
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [selectedRoleResourceId, setSelectedRoleResourceId] = useState<number | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [resourceIds, setResourceIds] = useState<number[]>([]);
  const [permissionIds, setPermissionIds] = useState<number[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<RoleResourcePermission | null>(null);

  const selectedRole = useMemo(
    () => vmsRoles.find((r) => r.roleId === selectedRoleId) ?? null,
    [vmsRoles, selectedRoleId]
  );

  const roleResourcesForRole = useMemo(() => {
    if (selectedRoleId == null) return [];
    return roleResources.filter((rr) => rr.roleId === selectedRoleId);
  }, [roleResources, selectedRoleId]);

  const selectedRoleResource = useMemo(
    () => roleResources.find((rr) => rr.roleResourceId === selectedRoleResourceId) ?? null,
    [roleResources, selectedRoleResourceId]
  );

  const mappingsForRoleResource = useMemo(() => {
    if (selectedRoleResourceId == null) return [];
    return roleResourcePermissions.filter(
      (m) => m.granteeRoleResourceId === selectedRoleResourceId
    );
  }, [roleResourcePermissions, selectedRoleResourceId]);

  const resourceById = useMemo(
    () => new Map(resources.map((r) => [r.resourceId, r])),
    [resources]
  );
  const permissionById = useMemo(
    () => new Map(permissions.map((p) => [p.permissionId, p])),
    [permissions]
  );

  const currentStep: Step =
    selectedRoleResourceId != null ? 3 : selectedRoleId != null ? 2 : 1;

  const goToRoles = () => {
    setSelectedRoleId(null);
    setSelectedRoleResourceId(null);
  };

  const goToRoleResources = () => {
    setSelectedRoleResourceId(null);
  };

  const openCreateMapping = () => {
    setResourceIds([]);
    setPermissionIds([]);
    setCreateOpen(true);
  };

  const toggle = (list: number[], id: number, checked: boolean) => {
    const s = new Set(list);
    if (checked) s.add(id);
    else s.delete(id);
    return Array.from(s).sort((a, b) => a - b);
  };

  const createDisabled =
    selectedRoleResourceId == null ||
    resourceIds.length === 0 ||
    permissionIds.length === 0;

  const handleCreateSubmit = () => {
    if (createDisabled || selectedRoleResourceId == null) return;
    onCreateMapping({
      roleResourceId: selectedRoleResourceId,
      resourceIds,
      permissionIds,
    });
    setCreateOpen(false);
    setResourceIds([]);
    setPermissionIds([]);
  };

  return (
    <div className="space-y-6">
      <SectionCard
        title="Access path"
        description="Walk the hierarchy: VMS role → role-resources under that role → permission mappings for each role-resource."
        actions={
          <button
            type="button"
            onClick={onOpenAdvancedMappings}
            className="text-sm font-medium text-brand hover:underline"
          >
            Open full mapping tools
          </button>
        }
      >
        {/* Step indicator */}
        <nav aria-label="Progress" className="mb-6 flex flex-wrap items-center gap-2 text-sm">
          <StepChip active={currentStep >= 1} done={currentStep > 1} label="1. Role" />
          <ChevronRight className="h-4 w-4 text-gray-300" />
          <StepChip active={currentStep >= 2} done={currentStep > 2} label="2. Role-resource" />
          <ChevronRight className="h-4 w-4 text-gray-300" />
          <StepChip active={currentStep >= 3} done={false} label="3. Permissions" />
        </nav>

        {/* Breadcrumb */}
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-gray-600">
          <button type="button" onClick={goToRoles} className="font-medium text-brand hover:underline">
            All roles
          </button>
          {selectedRole && (
            <>
              <ChevronRight className="h-4 w-4 text-gray-300" />
              <button
                type="button"
                onClick={goToRoleResources}
                className="font-medium text-brand hover:underline"
              >
                {selectedRole.role}
              </button>
            </>
          )}
          {selectedRoleResource && (
            <>
              <ChevronRight className="h-4 w-4 text-gray-300" />
              <span className="font-semibold text-gray-900">
                {selectedRoleResource.role}{" "}
                <span className="font-mono text-xs font-normal text-gray-400">
                  #{selectedRoleResource.roleResourceId}
                </span>
              </span>
            </>
          )}
        </div>

        {/* Step 1 */}
        <div className={currentStep === 1 ? "block" : "hidden"}>
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Shield className="h-4 w-4 text-brand" />
            Select a VMS role
          </h3>
          {vmsRoles.length === 0 ? (
            <EmptyState
              title="No roles loaded"
              description="Create roles under the Roles tab, then return here."
            />
          ) : (
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {vmsRoles.map((role) => (
                <li key={role.roleId}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedRoleId(role.roleId);
                      setSelectedRoleResourceId(null);
                    }}
                    className="flex w-full flex-col rounded-md border border-cardborder bg-white p-4 text-left transition hover:border-brand hover:shadow-sm"
                  >
                    <span className="font-medium text-gray-900">{role.role}</span>
                    <span className="mt-1 line-clamp-2 text-xs text-gray-500">
                      {role.description || "No description"}
                    </span>
                    <span className="mt-2 font-mono text-[11px] text-gray-400">ID {role.roleId}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Step 2 */}
        <div className={currentStep === 2 ? "block" : "hidden"}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <UserCog className="h-4 w-4 text-brand" />
              Role-resources for{" "}
              <StatusPill variant="info">{selectedRole?.role}</StatusPill>
            </h3>
            <button
              type="button"
              onClick={goToRoles}
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              ← Change role
            </button>
          </div>
          {roleResourcesForRole.length === 0 ? (
            <EmptyState
              title="No role-resources for this role"
              description={
                <>
                  Programs role-resources use <code className="rounded bg-gray-100 px-1">roleId</code>{" "}
                  to link to this VMS role. Create role-resources under the Role-resources tab, or they
                  may appear here after sub-user sync.
                </>
              }
            />
          ) : (
            <div className="overflow-x-auto rounded-md border border-cardborder">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2">ID</th>
                    <th className="px-4 py-2">Sub-role</th>
                    <th className="px-4 py-2">Resource</th>
                    <th className="px-4 py-2">Parent</th>
                    <th className="px-4 py-2 text-right">Next</th>
                  </tr>
                </thead>
                <tbody>
                  {roleResourcesForRole.map((rr) => {
                    const res = resourceById.get(rr.resourceId);
                    const mappingCount = roleResourcePermissions.filter(
                      (m) => m.granteeRoleResourceId === rr.roleResourceId
                    ).length;
                    return (
                      <tr key={rr.roleResourceId} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">{rr.roleResourceId}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">{rr.role}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {res ? `${res.resourceName} (#${rr.resourceId})` : `#${rr.resourceId}`}
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {rr.parentId == null ? "—" : rr.parentId}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => setSelectedRoleResourceId(rr.roleResourceId)}
                            className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand/90"
                          >
                            Permissions ({mappingCount})
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Step 3 */}
        <div className={currentStep === 3 ? "block" : "hidden"}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Link2 className="h-4 w-4 text-brand" />
              Role-resource permissions for{" "}
              <StatusPill variant="info">
                {selectedRoleResource?.role} #{selectedRoleResourceId}
              </StatusPill>
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={goToRoleResources}
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                ← Back to role-resources
              </button>
              <button
                type="button"
                onClick={openCreateMapping}
                disabled={isBusy || permissions.length === 0 || resources.length === 0}
                className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand/90 disabled:opacity-50"
              >
                <Plus className="h-3.5 w-3.5" />
                Add mappings
              </button>
            </div>
          </div>

          {mappingsForRoleResource.length === 0 ? (
            <EmptyState
              title="No permission mappings yet"
              description="Grant resources and permissions to this role-resource. Use Add mappings or the full Mappings tab."
              action={
                <button
                  type="button"
                  onClick={openCreateMapping}
                  disabled={isBusy || permissions.length === 0 || resources.length === 0}
                  className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                  Add mappings
                </button>
              }
            />
          ) : (
            <div className="overflow-x-auto rounded-md border border-cardborder">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2">Mapping ID</th>
                    <th className="px-4 py-2">Resource</th>
                    <th className="px-4 py-2">Permission</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mappingsForRoleResource.map((m) => {
                    const res = resourceById.get(m.resourceId);
                    const perm = permissionById.get(m.permissionId);
                    return (
                      <tr key={m.roleResourcePermissionId} className="border-t border-gray-100">
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">
                          {m.roleResourcePermissionId}
                        </td>
                        <td className="px-4 py-3 text-gray-800">
                          {res?.resourceName ?? "—"}{" "}
                          <span className="text-xs text-gray-400">#{m.resourceId}</span>
                        </td>
                        <td className="px-4 py-3">
                          {perm ? (
                            <StatusPill variant="info">
                              {perm.action}:{perm.scope}
                            </StatusPill>
                          ) : (
                            <span className="text-gray-500">#{m.permissionId}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(m)}
                            disabled={isBusy}
                            className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </SectionCard>

      <FormModal
        open={createOpen}
        title={`Add permission mappings · ${selectedRoleResource?.role ?? ""}`}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreateSubmit}
        submitting={
          selectedRoleResourceId != null &&
          matches("createRoleResourcePermission", selectedRoleResourceId)
        }
        submitDisabled={createDisabled}
        submitLabel={`Create ${resourceIds.length * permissionIds.length || 0} mapping(s)`}
        width="w-full max-w-2xl"
      >
        <p className="text-xs text-gray-500">
          Mappings are created for this role-resource:{" "}
          <strong>#{selectedRoleResourceId}</strong>. API builds the Cartesian product of selected
          resources and permissions (idempotent for duplicates).
        </p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <LabeledField label="Resources" required>
            <div className="max-h-52 overflow-auto rounded-md border border-cardborder">
              {resources.map((r) => (
                <label
                  key={r.resourceId}
                  className="flex cursor-pointer items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-0 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={resourceIds.includes(r.resourceId)}
                    onChange={(e) =>
                      setResourceIds((ids) => toggle(ids, r.resourceId, e.target.checked))
                    }
                  />
                  {r.resourceName}{" "}
                  <span className="text-xs text-gray-400">#{r.resourceId}</span>
                </label>
              ))}
            </div>
          </LabeledField>
          <LabeledField label="Permissions" required>
            <div className="max-h-52 overflow-auto rounded-md border border-cardborder">
              {permissions.map((p) => (
                <label
                  key={p.permissionId}
                  className="flex cursor-pointer items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-0 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={permissionIds.includes(p.permissionId)}
                    onChange={(e) =>
                      setPermissionIds((ids) => toggle(ids, p.permissionId, e.target.checked))
                    }
                  />
                  {p.action}:{p.scope}{" "}
                  <span className="text-xs text-gray-400">#{p.permissionId}</span>
                </label>
              ))}
            </div>
          </LabeledField>
        </div>
      </FormModal>

      <ConfirmDialog
        open={deleteTarget != null}
        title="Remove this permission mapping?"
        description={
          deleteTarget ? (
            <>
              Resource{" "}
              <strong>{resourceById.get(deleteTarget.resourceId)?.resourceName ?? deleteTarget.resourceId}</strong>{" "}
              · permission{" "}
              <strong>
                {permissionById.get(deleteTarget.permissionId)
                  ? `${permissionById.get(deleteTarget.permissionId)!.action}:${
                      permissionById.get(deleteTarget.permissionId)!.scope
                    }`
                  : `#${deleteTarget.permissionId}`}
              </strong>
            </>
          ) : null
        }
        destructive
        confirmLabel="Remove"
        loading={
          deleteTarget
            ? matches("deleteRoleResourcePermission", deleteTarget.roleResourcePermissionId)
            : false
        }
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            onDeleteMapping(deleteTarget.roleResourcePermissionId);
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
};

function StepChip({
  active,
  done,
  label,
}: {
  active: boolean;
  done: boolean;
  label: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
        done
          ? "bg-emerald-50 text-emerald-800"
          : active
            ? "bg-brand/15 text-brand"
            : "bg-gray-100 text-gray-500"
      }`}
    >
      <GitBranch className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}
